import type {
    UserImageCreate,
    UserImageInsert,
    UserImageSelect,
} from '~~/layers/base/types/userImage'
import { useApiBaseUrl } from '~~/layers/base/composables/useApiBaseUrl'
import { mergeCapacitorDesignerFetchOptions } from '~~/layers/base/utils/mergeCapacitorDesignerFetchOptions'

export const useUserImageService = () => {
    const apiBaseUrl = useApiBaseUrl()
    /**
     * POST multipart `image` to create a `user_image` row. Pass `sku` (designer flow) **or**
     * `handwriting_project_id` (handwritten hero preview — `sku` omitted).
     */
    const doCreateUserImage = async (
        params: Omit<UserImageCreate, 'cloudflare_image_id'> & { image: File | Blob }
    ) => {
        const formData = new FormData()
        formData.append('image', params.image)
        if (params.sku) formData.append('sku', params.sku)
        if (params.original_id) formData.append('original_id', params.original_id)
        if (params.design_project_id) formData.append('design_project_id', params.design_project_id)
        if (params.handwriting_project_id) {
            formData.append('handwriting_project_id', params.handwriting_project_id)
        }

        const data = ref<UserImageSelect | null>(null)
        const error = ref<unknown | null>(null)
        const pending = ref(true)

        try {
            data.value = await $fetch(
                '/api/user_image',
                await mergeCapacitorDesignerFetchOptions({
                    method: 'POST',
                    body: formData,
                    ...(apiBaseUrl && { baseURL: apiBaseUrl }),
                })
            )
        } catch (err) {
            error.value = err
        } finally {
            pending.value = false
        }

        return { data, error, pending, refresh: () => {} }
    }

    const doFetchUserImage = async (id: string) => {
        const data = ref<UserImageSelect | null>(null)
        const error = ref<unknown | null>(null)
        const pending = ref(true)

        try {
            data.value = await $fetch(
                `/api/user_image/${id}`,
                await mergeCapacitorDesignerFetchOptions({
                    ...(apiBaseUrl && { baseURL: apiBaseUrl }),
                })
            )
        } catch (err) {
            error.value = err
        } finally {
            pending.value = false
        }

        return { data, error, pending, refresh: () => {} }
    }

    const doFetchUserImageList = async (filters: { original?: boolean; sku?: string } = {}) => {
        const data = ref<UserImageSelect | null>(null)
        const error = ref<unknown | null>(null)
        const pending = ref(true)

        try {
            data.value = await $fetch(
                `/api/user_image`,
                await mergeCapacitorDesignerFetchOptions({
                    params: filters,
                    ...(apiBaseUrl && { baseURL: apiBaseUrl }),
                })
            )
        } catch (err) {
            error.value = err
        } finally {
            pending.value = false
        }

        return { data, error, pending, refresh: () => {} }
    }

    const doUpdateUserImage = async (id: string, body: Partial<UserImageInsert>) => {
        const data = ref<unknown | null>(null)
        const error = ref<unknown | null>(null)
        const pending = ref(true)

        try {
            data.value = await $fetch(
                `/api/user_image/${id}`,
                await mergeCapacitorDesignerFetchOptions({
                    method: 'PATCH',
                    body,
                    ...(apiBaseUrl && { baseURL: apiBaseUrl }),
                })
            )
        } catch (err) {
            error.value = err
        } finally {
            pending.value = false
        }

        return { data, error, pending, refresh: () => {} }
    }

    /**
     * Replace image bytes on an existing `user_image` row: multipart PATCH uploads to Cloudflare,
     * then updates `cloudflare_image_id` / `filename` on the row.
     */
    const doPatchUserImageWithImage = async (id: string, image: Blob) => {
        const formData = new FormData()
        formData.append('image', image)
        const data = ref<UserImageSelect | null>(null)
        const error = ref<unknown | null>(null)
        const pending = ref(true)

        try {
            data.value = await $fetch(
                `/api/user_image/${id}`,
                await mergeCapacitorDesignerFetchOptions({
                    method: 'PATCH',
                    body: formData,
                    ...(apiBaseUrl && { baseURL: apiBaseUrl }),
                })
            )
        } catch (err) {
            error.value = err
        } finally {
            pending.value = false
        }

        return { data, error, pending, refresh: () => {} }
    }

    return {
        doCreateUserImage,
        doFetchUserImage,
        doFetchUserImageList,
        doUpdateUserImage,
        doPatchUserImageWithImage,
    }
}
